import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function getPostLoginPath(redirect: string | null): string {
	// Only allow relative paths to prevent open redirect
	if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
		return redirect;
	}
	const onboarded = localStorage.getItem('clawdgate-onboarded');
	return onboarded ? '/dashboard' : '/getting-started';
}

export default function LoginPage() {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { login, loginWithGoogle, loginWithApple, resetPassword, isAuthenticated, isLoading } = useAuth();
	const [resetSent, setResetSent] = useState(false);

	const redirect = searchParams.get('redirect');

	// Redirect if already authenticated (handles Google redirect return)
	useEffect(() => {
		if (!isLoading && isAuthenticated) {
			navigate(getPostLoginPath(redirect), { replace: true });
		}
	}, [isLoading, isAuthenticated, navigate, redirect]);

	const message = (location.state as any)?.message;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');

		if (!email || !password) {
			setError('Email and password are required');
			return;
		}

		setLoading(true);

		try {
			await login(email, password);
			navigate(getPostLoginPath(redirect), { replace: true });
		} catch (err: any) {
			setError(err.message || 'Login failed');
		} finally {
			setLoading(false);
		}
	};

	const handleForgotPassword = async () => {
		if (!email) {
			setError('Please enter your email address first');
			return;
		}
		setError('');
		setLoading(true);
		try {
			await resetPassword(email);
			setResetSent(true);
		} catch (err: any) {
			setError(err.message || 'Failed to send reset email');
		} finally {
			setLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		setError('');
		setLoading(true);
		try {
			await loginWithGoogle();
			// If popup succeeded, onAuthStateChanged fires and the useEffect
			// above will navigate to /dashboard. If it fell back to redirect,
			// the page navigated away already.
		} catch (err: any) {
			setError(err.message || 'Google sign-in failed');
		} finally {
			setLoading(false);
		}
	};

	const handleAppleSignIn = async () => {
		setError('');
		setLoading(true);
		try {
			await loginWithApple();
		} catch (err: any) {
			setError(err.message || 'Apple sign-in failed');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
			{/* Animated background gradient orbs */}
			<div className="absolute top-0 -left-4 w-72 h-72 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
			<div className="absolute top-0 -right-4 w-72 h-72 bg-secondary-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
			<div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

			<div className="relative min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
				<div className="max-w-md w-full">
					{/* Logo & Title */}
					<div className="text-center mb-8">
						<div className="flex items-center justify-center gap-3 mb-4">
							<img src="/img/logo.png" alt="ClawdGate" className="w-14 h-14 rounded-2xl shadow-lg" />
							<span className="text-2xl font-light text-slate-300">+</span>
							<img src="/img/openclaw-logo.png" alt="OpenClaw" className="w-14 h-14 rounded-2xl shadow-lg" />
						</div>
						<h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">
							Welcome back
						</h1>
						<p className="text-slate-600">
							Sign in to manage your AI agent instances
						</p>
					</div>

					{/* Glass card */}
					<div className="backdrop-blur-lg bg-white/80 border border-white/20 rounded-2xl shadow-2xl p-8 space-y-6">
						{message && (
							<div className="rounded-xl bg-success-50/80 backdrop-blur-sm border border-success-200/50 p-4">
								<div className="flex items-center">
									<svg className="w-5 h-5 text-success-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
									</svg>
									<span className="text-sm font-medium text-success-800">{message}</span>
								</div>
							</div>
						)}

						{resetSent && (
							<div className="rounded-xl bg-success-50/80 backdrop-blur-sm border border-success-200/50 p-4">
								<div className="flex items-center">
									<svg className="w-5 h-5 text-success-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
									</svg>
									<span className="text-sm font-medium text-success-800">Password reset email sent. Check your inbox.</span>
								</div>
							</div>
						)}

						{error && (
							<div className="rounded-xl bg-danger-50/80 backdrop-blur-sm border border-danger-200/50 p-4">
								<div className="flex items-center">
									<svg className="w-5 h-5 text-danger-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
									</svg>
									<span className="text-sm font-medium text-danger-800">{error}</span>
								</div>
							</div>
						)}

						<form className="space-y-5" onSubmit={handleSubmit}>
							<div>
								<label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
									Email address
								</label>
								<input
									id="email"
									name="email"
									type="email"
									autoComplete="email"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									className="appearance-none block w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 bg-white/50 backdrop-blur-sm"
									placeholder="you@example.com"
								/>
							</div>

							<div>
								<label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
									Password
								</label>
								<input
									id="password"
									name="password"
									type="password"
									autoComplete="current-password"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="appearance-none block w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 bg-white/50 backdrop-blur-sm"
									placeholder="••••••••"
								/>
							</div>

							<div className="flex justify-end">
								<button
									type="button"
									onClick={handleForgotPassword}
									disabled={loading}
									className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 cursor-pointer disabled:opacity-50"
								>
									Forgot password?
								</button>
							</div>

							<button
								type="submit"
								disabled={loading}
								className="group relative w-full flex justify-center py-3 px-4 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 cursor-pointer"
							>
								<span className="absolute left-0 inset-y-0 flex items-center pl-3">
									<svg className="h-5 w-5 text-white/80 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
									</svg>
								</span>
								{loading ? 'Signing in...' : 'Sign in'}
							</button>
						</form>

						{/* Divider */}
						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-slate-200"></div>
							</div>
							<div className="relative flex justify-center text-sm">
								<span className="px-2 bg-white/80 text-slate-500">Or continue with</span>
							</div>
						</div>

						{/* Google Sign-In Button */}
						<button
							onClick={handleGoogleSignIn}
							disabled={loading}
							type="button"
							className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white/50 backdrop-blur-sm hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
						>
							<svg className="w-5 h-5" viewBox="0 0 24 24">
								<path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
								<path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
								<path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
								<path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
							</svg>
							Sign in with Google
						</button>

						{/* Apple Sign-In Button */}
						<button
							onClick={handleAppleSignIn}
							disabled={loading}
							type="button"
							className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white/50 backdrop-blur-sm hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
						>
							<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
								<path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
							</svg>
							Sign in with Apple
						</button>
					</div>

					{/* Footer link */}
					<p className="mt-6 text-center text-sm text-slate-600">
						Don't have an account?{' '}
						<Link
							to={'/register' + (redirect ? '?redirect=' + encodeURIComponent(redirect) : '')}
							className="font-semibold text-primary-600 hover:text-primary-700 transition-colors duration-200 cursor-pointer"
						>
							Create one now
						</Link>
					</p>
				</div>
			</div>
		</div>
	);
}
